import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "fieldType", "options", "validationRules", "fileRules", "optionsList", "createButtons", "editButtons", "modalTitle", "form", "previewModal"]
  static values = {
    templateId: String,
    mode: { type: String, default: "create" }
  }

  connect() {
    this.updateFieldOptions()
  }

  showModal() {
    // First remove hidden class
    this.modalTarget.classList.remove("hidden")

    // Add entrance animations
    const backdrop = this.modalTarget.querySelector(".fixed.inset-0.bg-gray-500\\/75")
    const modalPanel = this.modalTarget.querySelector(".transform.overflow-hidden.rounded-lg")

    // Ensure elements exist before adding animation classes
    if (backdrop) {
      backdrop.classList.add("opacity-0")
      setTimeout(() => {
        backdrop.classList.remove("opacity-0")
        backdrop.classList.add("opacity-100")
      }, 10)
    }

    if (modalPanel) {
      modalPanel.classList.add("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
      setTimeout(() => {
        modalPanel.classList.remove("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
        modalPanel.classList.add("opacity-100", "translate-y-0", "sm:scale-100")
      }, 10)
    }

    this.updateButtonsVisibility()
  }

  closeModal() {
    // Add exit animations
    const backdrop = this.modalTarget.querySelector(".fixed.inset-0.bg-gray-500\\/75")
    const modalPanel = this.modalTarget.querySelector(".transform.overflow-hidden.rounded-lg")

    // Ensure elements exist before adding animation classes
    if (backdrop) {
      backdrop.classList.remove("opacity-100")
      backdrop.classList.add("opacity-0", "ease-in", "duration-200")
    }

    if (modalPanel) {
      modalPanel.classList.remove("opacity-100", "translate-y-0", "sm:scale-100")
      modalPanel.classList.add("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95", "ease-in", "duration-200")
    }

    // Hide modal after animation completes
    setTimeout(() => {
      this.modalTarget.classList.add("hidden")
      this.resetForm()
    }, 200)
  }

  resetForm() {
    this.formTarget.reset()
    this.formTarget.action = `/forms/templates/${this.templateIdValue}/fields`

    // Reset the form method to POST for create mode
    const methodInput = this.formTarget.querySelector('input[name="_method"]')
    if (methodInput) {
      methodInput.value = 'post'
    }

    this.updateFieldOptions()
    this.modeValue = "create"
    this.updateButtonsVisibility()

    // Clear any dynamically added options
    const optionsList = this.optionsListTarget
    while (optionsList.children.length > 1) {
      optionsList.removeChild(optionsList.lastChild)
    }

    // Clear any form errors
    const formErrors = document.getElementById("form-errors")
    if (formErrors) {
      formErrors.innerHTML = ""
    }
  }

  updateFieldOptions() {
    const fieldType = this.fieldTypeTarget.value
    const hasOptions = ["select", "multiselect", "radio", "checkbox"].includes(fieldType)
    const hasValidation = ["text", "textarea", "number", "email", "phone"].includes(fieldType)
    const hasFileRules = ["file", "image"].includes(fieldType)

    this.optionsTarget.classList.toggle("hidden", !hasOptions)
    this.validationRulesTarget.classList.toggle("hidden", !hasValidation)
    this.fileRulesTarget.classList.toggle("hidden", !hasFileRules)
  }

  editField(event) {
    event.preventDefault()
    const fieldId = event.currentTarget.dataset.fieldId
    const field = this.element.querySelector(`tr[data-form-field-target="field"][data-field-id="${fieldId}"]`)

    // Populate form with field data
    this.formTarget.action = `/forms/templates/${this.templateIdValue}/fields/${fieldId}`

    // Set the form method to PATCH for edit mode
    const methodInput = this.formTarget.querySelector('input[name="_method"]')
    if (methodInput) {
      methodInput.value = 'patch'
    } else {
      const hiddenMethodInput = document.createElement('input')
      hiddenMethodInput.type = 'hidden'
      hiddenMethodInput.name = '_method'
      hiddenMethodInput.value = 'patch'
      this.formTarget.appendChild(hiddenMethodInput)
    }

    // Set basic field properties
    const fieldType = field.dataset.fieldType
    this.formTarget.querySelector("[name='form_field[field_type]']").value = fieldType
    this.formTarget.querySelector("[name='form_field[label]']").value = field.dataset.label
    this.formTarget.querySelector("[name='form_field[description]']").value = field.dataset.description

    // Debug the required value
    

    // Handle checkbox checked state
    const requiredCheckbox = this.formTarget.querySelector("[id='form_field_required']")
    const requiredValue = field.dataset.required
    requiredCheckbox.checked = !!requiredValue && /^(true|1|on|yes|checked)$/i.test(requiredValue.trim());

    // Handle field options for select, multiselect, radio, checkbox
    if (["select", "multiselect", "radio", "checkbox"].includes(fieldType) && field.dataset.options) {
      try {
        const options = JSON.parse(field.dataset.options)
        if (options.choices && Array.isArray(options.choices)) {
          // Clear existing options
          while (this.optionsListTarget.children.length > 0) {
            this.optionsListTarget.removeChild(this.optionsListTarget.lastChild)
          }

          // Add each option
          options.choices.forEach(choice => {
            const newOption = document.createElement("div")
            newOption.className = "flex items-center space-x-2"
            newOption.innerHTML = `
              <input type="text" name="options[choices][]" value="${choice}" class="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="Option text">
              <button type="button" class="text-gray-400 hover:text-red-500 transition-colors" data-action="click->form-field#removeOption">
                <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
              </button>
            `
            this.optionsListTarget.appendChild(newOption)
          })
        }
      } catch (e) {
        console.error("Error parsing options:", e)
      }
    }

    // Handle validation rules for text, textarea, number, email, phone
    if (["text", "textarea", "number", "email", "phone"].includes(fieldType) && field.dataset.validationRules) {
      try {
        const validationRules = JSON.parse(field.dataset.validationRules)

        if (validationRules.min_length) {
          this.formTarget.querySelector("[name='form_field[min_length]']").value = validationRules.min_length
        }

        if (validationRules.max_length) {
          this.formTarget.querySelector("[name='form_field[max_length]']").value = validationRules.max_length
        }

        if (validationRules.pattern) {
          this.formTarget.querySelector("[name='form_field[pattern]']").value = validationRules.pattern
        }
      } catch (e) {
        console.error("Error parsing validation rules:", e)
      }
    }

    // Handle file rules for file and image fields
    if (["file", "image"].includes(fieldType) && field.dataset.validationRules) {
      try {
        const validationRules = JSON.parse(field.dataset.validationRules)

        // Set allow_multiple checkbox
        const allowMultipleCheckbox = this.formTarget.querySelector("[id='form_field_allow_multiple']")
        if (allowMultipleCheckbox) {
          allowMultipleCheckbox.checked = !!validationRules.allow_multiple
        }

        // Set max_size (convert from bytes to MB for display)
        if (validationRules.max_size) {
          const maxSizeField = this.formTarget.querySelector("[name='form_field[max_size]']")
          if (maxSizeField) {
            maxSizeField.value = (validationRules.max_size / (1024 * 1024)).toFixed(1)
          }
        }

        // Set max_files
        if (validationRules.max_files) {
          const maxFilesField = this.formTarget.querySelector("[name='form_field[max_files]']")
          if (maxFilesField) {
            maxFilesField.value = validationRules.max_files
          }
        }

        // Set allowed_types (convert array to comma-separated string)
        if (validationRules.allowed_types && Array.isArray(validationRules.allowed_types)) {
          const allowedTypesField = this.formTarget.querySelector("[name='form_field[allowed_types]']")
          if (allowedTypesField) {
            allowedTypesField.value = validationRules.allowed_types.join(',')
          }
        }
      } catch (e) {
        console.error("Error parsing file validation rules:", e)
      }
    }

    // Update field options visibility based on field type
    this.updateFieldOptions()

    // Update mode and modal title
    this.modeValue = "edit"
    this.modalTitleTarget.textContent = "Edit Field"
    this.updateButtonsVisibility()

    // Show modal
    this.showModal()
  }

  updateButtonsVisibility() {
    if (this.modeValue === "create") {
      this.createButtonsTarget.classList.remove("!hidden")
      this.editButtonsTarget.classList.add("!hidden")
      this.modalTitleTarget.textContent = "Add Field"
    } else {
      this.createButtonsTarget.classList.add("!hidden")
      this.editButtonsTarget.classList.remove("!hidden")
      this.modalTitleTarget.textContent = "Edit Field"
    }
  }

  addOption(event) {
    event.preventDefault()
    const newOption = document.createElement("div")
    newOption.className = "flex items-center space-x-2"
    newOption.innerHTML = `
      <input type="text" name="options[choices][]" class="block w-full rounded-md border-0 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6" placeholder="Option text">
      <button type="button" class="text-gray-400 hover:text-red-500 transition-colors" data-action="click->form-field#removeOption">
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    `
    this.optionsListTarget.appendChild(newOption)

    // Focus the newly added input field
    const input = newOption.querySelector('input')
    if (input) {
      setTimeout(() => input.focus(), 10)
    }
  }

  removeOption(event) {
    event.preventDefault()
    event.currentTarget.closest(".flex").remove()
  }

  async submitAndAddMore(event) {
    event.preventDefault()

    try {
      // Determine the correct HTTP method based on the mode
      const method = this.modeValue === 'edit' ? 'PATCH' : 'POST'

      const response = await fetch(this.formTarget.action, {
        method: method,
        body: new FormData(this.formTarget),
        headers: {
          "Accept": "text/vnd.turbo-stream.html"
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.resetForm()
        // Keep the modal open but reset the form for adding another field
        this.showModal()
      } else {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        // Make sure the form-errors element is visible
        const formErrors = document.getElementById("form-errors")
        if (formErrors) {
          formErrors.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }

  async submitAndClose(event) {
    event.preventDefault()

    try {
      // Determine the correct HTTP method based on the mode
      const method = this.modeValue === 'edit' ? 'PATCH' : 'POST'

      const response = await fetch(this.formTarget.action, {
        method: method,
        body: new FormData(this.formTarget),
        headers: {
          "Accept": "text/vnd.turbo-stream.html"
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.closeModal()
      } else {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        // Make sure the form-errors element is visible
        const formErrors = document.getElementById("form-errors")
        if (formErrors) {
          formErrors.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error)
    }
  }

  async showPreview() {
    try {
      const response = await fetch(`/forms/templates/${this.templateIdValue}/preview`, {
        headers: {
          "Accept": "text/vnd.turbo-stream.html"
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        this.showPreviewModal()
      }
    } catch (error) {
      console.error("Error loading form preview:", error)
    }
  }

  showPreviewModal() {
    // First remove hidden class
    this.previewModalTarget.classList.remove("hidden")
    document.body.classList.add('overflow-hidden')

    // Add entrance animations
    const backdrop = this.previewModalTarget.querySelector(".fixed.inset-0.bg-gray-500\\/75")
    const modalPanel = this.previewModalTarget.querySelector(".transform.overflow-hidden.rounded-lg")

    // Ensure elements exist before adding animation classes
    if (backdrop) {
      backdrop.classList.add("opacity-0")
      requestAnimationFrame(() => {
        backdrop.classList.remove("opacity-0")
        backdrop.classList.add("opacity-100")
      })
    }

    if (modalPanel) {
      modalPanel.classList.add("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
      requestAnimationFrame(() => {
        modalPanel.classList.remove("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
        modalPanel.classList.add("opacity-100", "translate-y-0", "sm:scale-100")
      })
    }

    var resizeEvent = window.document.createEvent('UIEvents');
    resizeEvent.initUIEvent('resize', true, false, window, 0);
    window.dispatchEvent(resizeEvent);
  }

  closePreviewModal() {
    // Add exit animations
    const backdrop = this.previewModalTarget.querySelector(".fixed.inset-0.bg-gray-500\\/75")
    const modalPanel = this.previewModalTarget.querySelector(".transform.overflow-hidden.rounded-lg")

    // Ensure elements exist before adding animation classes
    if (backdrop) {
      backdrop.classList.remove("opacity-100")
      backdrop.classList.add("opacity-0", "ease-in", "duration-200")
    }

    if (modalPanel) {
      modalPanel.classList.remove("opacity-100", "translate-y-0", "sm:scale-100")
      modalPanel.classList.add("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95", "ease-in", "duration-200")
    }

    // Hide modal after animation completes
    setTimeout(() => {
      this.previewModalTarget.classList.add("hidden")
      document.body.classList.remove('overflow-hidden')
    }, 200)
  }
}
